import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  Button,
  Divider,
  Link,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { DAMAGE_REASON_LABEL } from 'src/types/damage-reason';
import { IPackagingDamageDispatchDetailedItem } from 'src/types/packaging-damage-dispatch';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemCode', label: 'Item Code', align: 'left', primary: true },
  { id: 'itemName', label: 'Item Name', align: 'left', primary: true, minWidth: 220 },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
  { id: 'damageReason', label: 'Damage Reason', align: 'left', primary: true },
  { id: 'unitCost', label: 'Unit Cost', align: 'right', primary: true },
  { id: 'totalDamageValue', label: 'Total Value', align: 'right', primary: true },
];

type Props = {
  currentDamageDispatch: IPackagingDamageDispatchDetailedItem;
};

export default function PackagingDamageDispatchDetails({
  currentDamageDispatch,
}: Props) {
  const totalDamageValue = currentDamageDispatch.items.reduce(
    (sum, item) => sum + (item.totalDamageValue ?? 0),
    0
  );

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid
              mb={1}
              display="flex"
              mt={2}
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              <Typography variant="subtitle2">Basic Information</Typography>
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={currentDamageDispatch.damageCode}
                secondary="Damage No"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fDateTime(currentDamageDispatch.damageDate)}
                secondary="Damage Date"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={currentDamageDispatch.items.length}
                secondary="Item Lines"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            {currentDamageDispatch.sourceReturn && (
              <Grid xs={12}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Label variant="soft" color="warning">
                    Auto-created from Return
                  </Label>
                  <Link
                    component={RouterLink}
                    href={paths.dashboard.packagingReturn.view(
                      currentDamageDispatch.sourceReturn._id
                    )}
                  >
                    {currentDamageDispatch.sourceReturn.returnCode}
                  </Link>
                  <Typography variant="caption" color="text.disabled">
                    — locked, can only be reversed by deleting that return
                  </Typography>
                </Stack>
              </Grid>
            )}

            {currentDamageDispatch.createdBy && (
              <Grid xs={12} md={6}>
                <ListItemText
                  primary={`${currentDamageDispatch.createdBy.firstName}${
                    currentDamageDispatch.createdBy.lastName
                      ? ` ${currentDamageDispatch.createdBy.lastName}`
                      : ''
                  }`}
                  secondary="Recorded By"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>
            )}

            <Grid xs={12} md={6}>
              <ListItemText
                primary={fCurrencyRupees(totalDamageValue * 100)}
                secondary="Total Damage Value"
                primaryTypographyProps={{ typography: 'body2', color: 'error.main' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            {currentDamageDispatch.remarks && (
              <Grid xs={12}>
                <ListItemText
                  primary={currentDamageDispatch.remarks}
                  secondary="Remarks"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>
            )}

            <Divider
              sx={{
                width: 1,
                my: 2,
                borderStyle: 'dashed',
              }}
            />

            <Grid mb={1} xs={12} width="full">
              <Typography variant="subtitle2">Line Items</Typography>
            </Grid>

            <Grid xs={12}>
              <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                <Scrollbar>
                  <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
                    <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                    <TableBody>
                      {currentDamageDispatch.items.map((item) => (
                        <TableRow hover key={item._id}>
                          <TableCell align="left">
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.packagingItem.view(item.packagingItem._id)}
                            >
                              {item.packagingItem.itemCode}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.packagingItem.itemName}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.quantity}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {DAMAGE_REASON_LABEL[item.damageReason] ?? item.damageReason}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {item.unitCost != null
                              ? fCurrencyRupees(item.unitCost * 100)
                              : 'N/A'}
                          </TableCell>

                          <TableCell
                            sx={{
                              whiteSpace: 'nowrap',
                              textAlign: 'right',
                              color: 'error.main',
                            }}
                          >
                            {item.totalDamageValue != null
                              ? fCurrencyRupees(item.totalDamageValue * 100)
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={3}>
            <Link component={RouterLink} href={paths.dashboard.packagingDamageDispatch.root}>
              <Button variant="outlined" color="inherit">
                Back
              </Button>
            </Link>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
